import { useRef } from 'react';
import axios from 'axios';
import { useUnmount } from 'ahooks';
import { CANCEL_FLAG_MSG } from '@intelligence/utils/constant';

import type { CancelToken } from 'axios';

const { CancelToken: AxiosCancelToken } = axios;

export interface IRequestToken {
  cancelToken: CancelToken;
  isStale: () => boolean;
}

/**
 * 请求取消 + 竞态防护 Hook
 *
 * 结合 cancelToken（HTTP 层取消，基于 intelligence 的 useCancelTokenRequest 设计）
 * 和版本计数器（可靠的陈旧结果守卫）。
 *
 * 用法：
 *   const { next } = useCancelRequest();
 *
 *   // 在 update() 中调用 next()，同时取消旧请求并生成新版本令牌
 *   const token = next();
 *   fetchData(params, token);
 *
 *   // 在 fetchData 内用 cancelToken 传入服务，用 isStale() 守卫状态更新
 *   const res = await someService(params, { cancelToken: token.cancelToken });
 *   if (token.isStale()) {
 *     return;
 *   }
 *   setState(res);
 */
export default function useCancelRequest(): { next: () => IRequestToken } {
  const versionRef = useRef(0);
  const cancelerRef = useRef<((...args: any[]) => void) | null>(null);

  const next = (): IRequestToken => {
    if (cancelerRef.current) {
      cancelerRef.current(CANCEL_FLAG_MSG);
      cancelerRef.current = null;
    }

    versionRef.current = versionRef.current + 1;
    const version = versionRef.current;

    const { token, cancel } = AxiosCancelToken.source();
    cancelerRef.current = cancel;

    return {
      cancelToken: token,
      isStale: () => version !== versionRef.current,
    };
  };

  useUnmount(() => {
    cancelerRef.current?.(CANCEL_FLAG_MSG);
    cancelerRef.current = null;
  });

  return { next };
}
